// The installed desktop app (Tauri), Oct 2026 — only rendered inside it:
//   requirement 13  DesktopRemindersWatcher + DesktopNotificationsCard: native notifications while the app runs
//   requirement 19  DesktopUpdateWatcher + DesktopUpdatesCard: signed one-click updates (inert when the build has no key)
import { useEffect, useRef, useState } from 'react';
import { renewalQueue, type Task } from '@vero/domain';
import { takeSnapshot } from '@vero/backup';
import { useRuntime, type Runtime } from '../core/runtime.tsx';
import { Modal } from '../core/ui.tsx';
import { Icon } from '../core/icons.tsx';
import { RELEASE_NOTES } from '@vero/platform';
import { UpdateNotes, useServedNotes } from './whats-new.tsx';
import {
  addLocalHistory, dueLocalReminders, isDesktopApp, loadLocalPrefs, localHistory, nativePermission, saveLocalPrefs, showNative, toldStore, desktopInvoke,
  MINUTES_CHOICES, type LocalReminderPrefs, type NativePermission, type ReminderTexts,
} from '../core/local-reminders.ts';

type SelEv = { target: HTMLSelectElement };
type InEv = { target: HTMLInputElement };

function texts(rt: Runtime): ReminderTexts {
  const t = rt.t;
  return {
    appointmentTitle: (min) => t('desktop.notify.appt_title', { min }),
    appointmentBody: (time, title) => t('desktop.notify.appt_body', { time, title }),
    digestTitle: t('desktop.notify.digest_title'),
    due: (n) => t('desktop.notify.due', { n }),
    overdue: (n) => t('desktop.notify.overdue', { n }),
    renewals: (n) => t('desktop.notify.renewals', { n }),
  };
}
function usePrefs(): [LocalReminderPrefs, (p: LocalReminderPrefs) => void] {
  const [p, setP] = useState<LocalReminderPrefs>(loadLocalPrefs);
  useEffect(() => { const on = () => setP(loadLocalPrefs()); window.addEventListener('vero:local-reminders', on); return () => window.removeEventListener('vero:local-reminders', on); }, []);
  return [p, (n) => { setP(n); saveLocalPrefs(n); }];
}

/** every minute while the app runs: the reminders that are due now, shown once each */
export function DesktopRemindersWatcher() {
  const rt = useRuntime();
  const [prefs] = usePrefs();
  const rtRef = useRef(rt); rtRef.current = rt;
  useEffect(() => {
    if (!isDesktopApp() || !prefs.on) return;
    let running = false;
    const check = async () => {
      if (running) return;
      running = true;
      try {
        const r = rtRef.current;
        const told = toldStore();
        const tasks = r.view.rows('tasks') as readonly Task[];
        const renewals = prefs.renewals ? renewalQueue(r.view, r.today).map((x) => ({ policyId: x.policy.id, dueDate: x.dueDate, days: x.days })) : [];
        for (const n of dueLocalReminders({ now: new Date(), tasks, renewals, prefs, told: told.has, texts: texts(r) })) {
          told.add(n.key);
          if (!n.title) continue; // a day with nothing to tell
          const ok = await showNative(n.title, n.body);
          if (ok) for (const k of n.also ?? []) told.add(k);
          addLocalHistory({ at: new Date().toISOString(), kind: n.kind, title: n.title, body: n.body, ok });
        }
        told.save();
      } catch (e) { console.warn('[reminders]', e); } finally { running = false; }
    };
    const first = setTimeout(() => void check(), 5_000);
    const every = setInterval(() => void check(), 60_000);
    return () => { clearTimeout(first); clearInterval(every); };
  }, [prefs]);
  return null;
}

/** Settings → «Ειδοποιήσεις σε αυτόν τον υπολογιστή» (desktop app only) */
export function DesktopNotificationsCard() {
  const rt = useRuntime();
  const { t } = rt;
  const [prefs, setPrefs] = usePrefs();
  const [perm, setPerm] = useState<NativePermission | null>(null);
  const [hist, setHist] = useState(localHistory);
  useEffect(() => { void nativePermission(false).then(setPerm); }, []);
  if (!isDesktopApp()) return null;
  const set = (patch: Partial<LocalReminderPrefs>) => setPrefs({ ...prefs, ...patch });
  const turnOn = async () => {
    const p = await nativePermission(true);
    setPerm(p);
    if (p === 'denied' || p === 'unavailable') { rt.toast(t(p === 'denied' ? 'desktop.notify.denied' : 'desktop.notify.unavailable'), 'error'); return; }
    set({ on: true });
    await test();
  };
  const test = async () => {
    const title = t('desktop.notify.test_title'), body = t('desktop.notify.test_body');
    const ok = await showNative(title, body);
    addLocalHistory({ at: new Date().toISOString(), kind: 'test', title, body, ok });
    setHist(localHistory());
    rt.toast(t(ok ? 'desktop.notify.test_sent' : 'desktop.notify.test_failed'), ok ? 'success' : 'error');
  };
  const quiet = !!(prefs.quietFrom && prefs.quietTo);
  return (
    <section className="card card-pad" data-testid="desktop-notify">
      <h3 style={{ margin: '0 0 8px' }}>{t('desktop.notify.title')}</h3>
      <p className="small" style={{ marginTop: 0 }}>{t('desktop.notify.intro')}</p>
      {prefs.on ? <div className="row wrap" style={{ gap: 8 }}>
        <span className="chip ok">{t('desktop.notify.on_state')}</span>
        <button className="btn btn-sm" onClick={() => void test()} data-testid="desktop-notify-test">{t('push.test')}</button>
        <button className="btn btn-sm btn-ghost" onClick={() => set({ on: false })}>{t('push.off')}</button>
      </div> : <button className="btn btn-sm btn-primary" onClick={() => void turnOn()} data-testid="desktop-notify-on">{t('desktop.notify.turn_on')}</button>}
      {perm === 'denied' && <div className="callout warn small" style={{ marginTop: 8 }}>{t('desktop.notify.denied')}</div>}
      {prefs.on && <div className="stack" style={{ marginTop: 10, gap: 6 }}>
        <div className="row wrap small" style={{ gap: 12 }}>
          {(['appointments', 'tasks', 'renewals'] as const).map((k) => <label key={k} className="row" style={{ gap: 4 }}><input type="checkbox" checked={prefs[k]} onChange={(e: InEv) => set({ [k]: e.target.checked })} />{t(`push.pref.${k}`)}</label>)}
          {prefs.appointments && <label className="row" style={{ gap: 4 }}>{t('push.minutes_before')}<select className="input input-sm" value={prefs.minutesBefore} onChange={(e: SelEv) => set({ minutesBefore: Number(e.target.value) })}>{MINUTES_CHOICES.map((m) => <option key={m} value={m}>{t('push.minutes', { n: m })}</option>)}</select></label>}
        </div>
        <QuietHours from={prefs.quietFrom} to={prefs.quietTo} onChange={(quietFrom, quietTo) => set({ quietFrom, quietTo })} />
        {quiet && <div className="tiny muted">{t('push.quiet_hint')}</div>}
      </div>}
      <p className="tiny muted">{t('desktop.notify.limits')}</p>
      {hist.length > 0 && <details className="small" style={{ marginTop: 6 }}>
        <summary>{t('push.history')}</summary>
        <div className="agenda-list" style={{ marginTop: 6 }}>{hist.slice(0, 20).map((h, i) => <div key={i} className="agenda-item" style={{ gridTemplateColumns: 'minmax(0,1fr)' }}><div className="t">{h.title}{!h.ok && <span className="chip high" style={{ marginInlineStart: 6 }}>{t('push.status.failed')}</span>}</div><div className="s" style={{ whiteSpace: 'pre-wrap' }}>{rt.i18n.dateTime(h.at)} · {h.body}</div></div>)}</div>
      </details>}
    </section>
  );
}

/** quiet hours: off, or from–to (local time of this device) */
export function QuietHours({ from, to, onChange }: { from: string | null; to: string | null; onChange: (from: string | null, to: string | null) => void }) {
  const rt = useRuntime();
  const on = !!(from && to);
  return (
    <div className="row wrap small" style={{ gap: 8, alignItems: 'center' }} data-testid="quiet-hours">
      <label className="row" style={{ gap: 4 }}><input type="checkbox" checked={on} onChange={(e: InEv) => onChange(e.target.checked ? '22:00' : null, e.target.checked ? '07:30' : null)} />{rt.t('push.quiet')}</label>
      {on && <>
        <label className="row" style={{ gap: 4 }}>{rt.t('push.quiet_from')}<input className="input input-sm" type="time" value={from ?? ''} onChange={(e: InEv) => /^\d\d:\d\d$/.test(e.target.value) && onChange(e.target.value, to)} /></label>
        <label className="row" style={{ gap: 4 }}>{rt.t('push.quiet_to')}<input className="input input-sm" type="time" value={to ?? ''} onChange={(e: InEv) => /^\d\d:\d\d$/.test(e.target.value) && onChange(from, e.target.value)} /></label>
      </>}
    </div>
  );
}

// --- updates (requirement 19) -------------------------------------------------------------------------------------------
export interface UpdateInfo { version: string; currentVersion: string; notes: string | null; date: number | null }
export interface UpdateStatus { enabled: boolean; currentVersion: string; update: UpdateInfo | null }
const SKIP_KEY = 'vero.update.later';
const readSkip = (): { version: string; until: number } | null => { try { return JSON.parse(localStorage.getItem(SKIP_KEY) ?? 'null') as { version: string; until: number } | null; } catch { return null; } };
export async function checkForUpdate(): Promise<UpdateStatus | null> {
  const inv = desktopInvoke();
  if (!inv) return null;
  return await inv('update_check') as UpdateStatus;
}

/** at start (after 20 s), when the computer comes back online and every 6 hours: a newer signed version → the popup */
export function DesktopUpdateWatcher() {
  const rt = useRuntime();
  const rtRef = useRef(rt); rtRef.current = rt;
  useEffect(() => {
    if (!isDesktopApp()) return;
    let shown = '';
    const run = async () => {
      if (typeof navigator !== 'undefined' && navigator.onLine === false) return;
      try {
        const s = await checkForUpdate();
        const u = s?.update;
        if (!s?.enabled || !u || shown === u.version) return;
        const skip = readSkip();
        if (skip && skip.version === u.version && skip.until > Date.now()) return;
        shown = u.version;
        rtRef.current.open(<UpdateModal info={u} />);
      } catch (e) { console.warn('[update] check failed', e); /* offline or the server is down: tried again later */ }
    };
    const first = setTimeout(() => void run(), 20_000);
    const every = setInterval(() => void run(), 6 * 3600_000);
    const online = () => void run();
    window.addEventListener('online', online);
    return () => { clearTimeout(first); clearInterval(every); window.removeEventListener('online', online); };
  }, []);
  return null;
}

const errKey = (e: unknown) => { const s = String((e as Error)?.message ?? e); return s.includes('signature') ? 'desktop.update.err_signature' : s.startsWith('update.busy') ? 'desktop.update.err_busy' : 'desktop.update.err_generic'; };

export function UpdateModal({ info }: { info: UpdateInfo }) {
  const rt = useRuntime();
  const { t } = rt;
  const [phase, setPhase] = useState<'ask' | 'saving' | 'download' | 'error'>('ask');
  const [pct, setPct] = useState<number | null>(null);
  const [err, setErr] = useState('');
  const install = async () => {
    const inv = desktopInvoke();
    if (!inv) return;
    setErr('');
    try {
      // a recovery copy of everything first (the update never touches the data folder; this is the second safety net)
      setPhase('saving');
      await takeSnapshot(rt.ws, t('desktop.update.snapshot_label', { version: info.version }), 'manual');
      setPhase('download');
      const poll = setInterval(() => { void (inv('update_progress') as Promise<[number, number | null]>).then(([n, total]) => setPct(total ? Math.min(100, Math.round((n / total) * 100)) : null)).catch(() => undefined); }, 500);
      try { await inv('update_install'); } finally { clearInterval(poll); }
      // on success the app restarts (macOS) or the installer closes it (Windows); nothing more happens here
    } catch (e) { console.warn('[update] install failed', e); setErr(t(errKey(e))); setPhase('error'); }
  };
  const later = () => { try { localStorage.setItem(SKIP_KEY, JSON.stringify({ version: info.version, until: Date.now() + 24 * 3600_000 })); } catch { /* storage blocked */ } rt.close(); };
  const busy = phase === 'saving' || phase === 'download';
  // requirement 20: what the offered version brings, filtered by this device's plan (the platform's notes file, which
  // already describes the new release; the GitHub release text stays available below)
  const served = useServedNotes();
  return (
    <Modal title={t('desktop.update.title')} footer={<>
      <button className="btn" disabled={busy} onClick={later} data-testid="update-later">{t('desktop.update.later')}</button>
      <button className="btn btn-primary" disabled={busy} onClick={() => void install()} data-testid="update-install"><Icon name="download" />{t(phase === 'error' ? 'desktop.update.retry' : 'desktop.update.install')}</button>
    </>}>
      <div data-testid="update-modal">
        <p style={{ marginTop: 0 }}><b>{t('desktop.update.available', { version: info.version, current: info.currentVersion })}</b>{info.date ? <span className="small muted"> · {rt.i18n.date(new Date(info.date * 1000).toISOString().slice(0, 10))}</span> : null}</p>
        <UpdateNotes target={info.version} notes={served?.notes ?? RELEASE_NOTES} />
        {info.notes && <details open={!served} style={{ marginTop: 6 }}><summary className="small muted" style={{ marginBottom: 4 }}>{t('desktop.update.notes')}</summary><div className="callout small" style={{ whiteSpace: 'pre-wrap', maxHeight: 260, overflow: 'auto' }} data-testid="update-notes">{info.notes}</div></details>}
        <p className="small muted">{t('notes.binary_vs_rights')}</p>
        <p className="small">{t('desktop.update.safe')}</p>
        {phase === 'saving' && <div className="callout small" role="status">{t('desktop.update.saving')}</div>}
        {phase === 'download' && <div className="callout small" role="status">{t('desktop.update.downloading')}{pct !== null ? ` ${pct}%` : ''}</div>}
        {phase === 'error' && <div className="callout crit small" role="alert">{err}</div>}
      </div>
    </Modal>
  );
}

/** Settings → «Ενημερώσεις εφαρμογής» (desktop app only): version, check now, where the data lives */
export function DesktopUpdatesCard() {
  const rt = useRuntime();
  const { t } = rt;
  const [st, setSt] = useState<UpdateStatus | null>(null);
  const [busy, setBusy] = useState(false);
  const [path, setPath] = useState<string | null>(null);
  useEffect(() => { const inv = desktopInvoke(); if (inv) void (inv('app_data_path') as Promise<string>).then(setPath).catch(() => undefined); }, []);
  if (!isDesktopApp()) return null;
  const check = async () => {
    setBusy(true);
    try {
      const s = await checkForUpdate();
      setSt(s);
      if (s?.update) rt.open(<UpdateModal info={s.update} />);
      else rt.toast(t(s?.enabled ? 'desktop.update.none' : 'desktop.update.not_configured'), 'info');
    } catch (e) { console.warn('[update]', e); rt.toast(t('desktop.update.err_check'), 'error'); } finally { setBusy(false); }
  };
  return (
    <section className="card card-pad" data-testid="desktop-updates">
      <h3 style={{ margin: '0 0 8px' }}>{t('desktop.update.card_title')}</h3>
      <p className="small" style={{ marginTop: 0 }}>{t('desktop.update.intro')}</p>
      {st && <p className="small">{t('desktop.update.current', { version: st.currentVersion })}</p>}
      <button className="btn btn-sm" disabled={busy} onClick={() => void check()} data-testid="desktop-update-check">{t('desktop.update.check')}</button>
      {path && <p className="tiny muted">{t('desktop.update.data_path', { path })}</p>}
    </section>
  );
}
